import { v } from 'convex/values';
import type { ModuleKey } from '@bill-alps/backend/features/companies/modules';

export const MODULE_PERMISSIONS = ['none', 'read', 'write'] as const;
export type ModulePermission = (typeof MODULE_PERMISSIONS)[number];

export const modulePermissionValidator = v.union(
  v.literal('none'),
  v.literal('read'),
  v.literal('write'),
);

export const COMPANY_MEMBER_ROLES = ['admin', 'member'] as const;
export type CompanyMemberRole = (typeof COMPANY_MEMBER_ROLES)[number];
export type EffectiveCompanyRole = 'owner' | CompanyMemberRole;

export const companyMemberRoleValidator = v.union(
  v.literal('admin'),
  v.literal('member'),
);

export interface ModulePermissions {
  invoicing: ModulePermission;
  payroll: ModulePermission;
  // Optional until the merge backfill (maintenance/migrations/modulePermissions)
  // has rewritten every membership/invitation row; missing means 'none'.
  accounting?: ModulePermission;
}

/**
 * A stored row or a stale client payload, which may still carry the two
 * pre-merge keys. Only `normalizeModulePermissions` reads that shape.
 */
export type StoredModulePermissions = ModulePermissions & {
  expenses?: ModulePermission;
  financialStatements?: ModulePermission;
};

export const modulePermissionsValidator = v.object({
  invoicing: modulePermissionValidator,
  payroll: modulePermissionValidator,
  accounting: v.optional(modulePermissionValidator),
  // Pre-merge keys, kept optional for the rollout so a stale client still
  // validates. Dropped once every row has been normalised.
  expenses: v.optional(modulePermissionValidator),
  financialStatements: v.optional(modulePermissionValidator),
});

export const NO_MODULE_PERMISSIONS: ModulePermissions = {
  invoicing: 'none',
  payroll: 'none',
  accounting: 'none',
};

export const WRITE_MODULE_PERMISSIONS: ModulePermissions = {
  invoicing: 'write',
  payroll: 'write',
  accounting: 'write',
};

/**
 * Canonicalizes a permissions object: merges the two pre-merge keys into
 * `accounting` (the strongest of the two wins) and drops them. Every write
 * path runs through this so legacy rows and stale-client payloads are lazily
 * migrated.
 *
 * When the halves are present they WIN over any `accounting` value, which at
 * that point can only be the retired pre-split key (the split deliberately
 * started every company fresh). When they are absent the row is already
 * post-merge and `accounting` is kept — the two shapes are otherwise
 * indistinguishable, which is why the merge backfill must only run once the
 * split backfill (`maintenance/migrations/modulePermissions`) has rewritten
 * every pre-split row.
 */
export function normalizeModulePermissions(
  input: StoredModulePermissions,
): ModulePermissions {
  const halves =
    input.expenses !== undefined || input.financialStatements !== undefined
      ? strongestPermission(
          input.expenses ?? 'none',
          input.financialStatements ?? 'none',
        )
      : undefined;
  return {
    invoicing: input.invoicing,
    payroll: input.payroll,
    accounting: halves ?? input.accounting ?? 'none',
  };
}

function strongestPermission(
  a: ModulePermission,
  b: ModulePermission,
): ModulePermission {
  return PERMISSION_RANK[a] >= PERMISSION_RANK[b] ? a : b;
}

const PERMISSION_RANK: Record<ModulePermission, number> = {
  none: 0,
  read: 1,
  write: 2,
};

export function permissionIncludes(
  actual: ModulePermission,
  required: Exclude<ModulePermission, 'none'>,
) {
  return PERMISSION_RANK[actual] >= PERMISSION_RANK[required];
}

export function effectiveModulePermission(
  role: EffectiveCompanyRole,
  permissions: ModulePermissions,
  module: ModuleKey,
): ModulePermission {
  // `?? 'none'`: a pre-merge row lacks `accounting` — fresh-start fallback.
  return role === 'owner' || role === 'admin'
    ? 'write'
    : (permissions[module] ?? 'none');
}

export function hasAnyModuleAccess(permissions: ModulePermissions) {
  // Explicit check: with optional keys, `undefined !== 'none'` would wrongly
  // count a missing key as access.
  return Object.values(permissions).some(
    (permission) => permission === 'read' || permission === 'write',
  );
}
